import { isValidPhoneNumber } from 'react-phone-number-input'
import { z } from 'zod'

import { REGIONS_BY_ID } from '@snootsvet/shared'

import { useAppForm } from '@/frontend/components/form'
import { Button } from '@/frontend/components/ui/button'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { RegionFlag } from '@/frontend/features/region/region-flag'
import { useCreateMember } from '@/frontend/hooks/api/use-create-member'
import { type StripeIdValidationResult, validateStripeId } from '@/frontend/lib/stripe'

import { US_STATES } from './us-states'

// Mirrors postMemberBodySchema from shared. Optional fields are `.optional()` (not `z.string()`)
// so that fields left untouched — which TanStack Form reports as `undefined` given our empty
// `defaultValues` — pass validation. Empty strings are converted to undefined on submit.
const createMemberSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.email('A valid email is required'),
  phone: z.string().refine(isValidPhoneNumber, 'Please enter a valid phone number'),
  address1: z.string().optional(),
  address2: z.string().optional(),
  city: z.string().optional(),
  postalCode: z.string().optional(),
  // Combobox: `null` when cleared, `undefined` when untouched.
  state: z.string().nullish(),
  country: z.string().optional(),
  // Optional — reject only a non-customer ID (e.g. a subscription ID) pasted into the field.
  stripeCustomerId: z
    .string()
    .optional()
    .superRefine((v, ctx) => {
      if (!v) return
      const message = getStripeCustomerIdError(validateStripeId(v, 'customer'))
      if (message) ctx.addIssue({ code: 'custom', message })
    }),
  internalNotes: z.string().optional(),
})

type CreateMemberFormValues = z.infer<typeof createMemberSchema>

/** Maps Stripe customer ID validation results to form field copy. */
function getStripeCustomerIdError(result: StripeIdValidationResult): string | null {
  if (result === 'valid') return null
  if (result === 'wrong_entity') {
    return 'That appears to be a Stripe subscription ID. Members should be linked to a Stripe customer ID beginning with cus_.'
  }
  return 'Enter a Stripe customer ID beginning with cus_.'
}

/** Form for creating a new member. */
export function CreateMemberForm({
  onSuccess,
  onCancel,
}: {
  onSuccess: () => void
  onCancel: () => void
}) {
  const { mutateAsync: createMember } = useCreateMember()
  // Region is derived from the active region (not a form field). Surface it read-only
  // so staff can see which region the member will be filed under before submitting.
  const { region } = useViewerRegion()

  const form = useAppForm({
    // `phone` defaults to '' so the PhoneInput has a controlled value from the start
    // and the `isValidPhoneNumber` refine sees a string on submit.
    defaultValues: { phone: '' } as CreateMemberFormValues,
    validators: { onSubmit: createMemberSchema },
    onSubmit: async ({ value, formApi }) => {
      try {
        await createMember({
          firstName: value.firstName,
          lastName: value.lastName,
          email: value.email,
          phone: value.phone,
          address1: value.address1 || undefined,
          address2: value.address2 || undefined,
          city: value.city || undefined,
          postalCode: value.postalCode || undefined,
          // Country and state are mutually exclusive by region: US members have a state
          // (2-letter code) and no country; UK members have a country and no state.
          state: region === 'us' ? value.state || undefined : undefined,
          country: region === 'us' ? undefined : value.country || undefined,
          stripeCustomerId: value.stripeCustomerId || undefined,
          internalNotes: value.internalNotes || undefined,
        })
        onSuccess()
      } catch (err) {
        formApi.setErrorMap({
          onSubmit: {
            form: err instanceof Error ? err.message : 'Failed to add member',
            fields: {},
          },
        })
      }
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <div className="flex flex-col gap-4 py-2">
          <form.GlobalError />

          <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <RegionFlag region={region} /> Added to the {REGIONS_BY_ID[region].name} region.
          </p>

          <div className="flex gap-3">
            <form.AppField name="firstName">
              {(field) => (
                <field.InputField
                  label="First name"
                  placeholder="Jane"
                  layoutClassName="flex-1"
                  required
                />
              )}
            </form.AppField>
            <form.AppField name="lastName">
              {(field) => (
                <field.InputField
                  label="Last name"
                  placeholder="Doe"
                  layoutClassName="flex-1"
                  required
                />
              )}
            </form.AppField>
          </div>

          <div className="flex gap-3">
            <form.AppField name="email">
              {(field) => (
                <field.InputField
                  label="Email"
                  type="email"
                  placeholder="jane@example.com"
                  layoutClassName="flex-1"
                  required
                />
              )}
            </form.AppField>
            <form.AppField name="phone">
              {(field) => (
                <field.PhoneInputField
                  label="Phone"
                  autoComplete="tel"
                  defaultCountry={region === 'us' ? 'US' : 'GB'}
                  layoutClassName="flex-1"
                  required
                />
              )}
            </form.AppField>
          </div>

          <form.AppField name="address1">
            {(field) => <field.InputField label="Address line 1" placeholder="1 High Street" />}
          </form.AppField>

          <form.AppField name="address2">
            {(field) => <field.InputField label="Address line 2" placeholder="Apartment 4" />}
          </form.AppField>

          <div className="flex gap-3">
            <form.AppField name="city">
              {(field) => (
                <field.InputField label="City" placeholder="London" layoutClassName="flex-1" />
              )}
            </form.AppField>
            <form.AppField name="postalCode">
              {(field) => (
                <field.InputField
                  label={region === 'us' ? 'ZIP code' : 'Postal code'}
                  placeholder={region === 'us' ? '07030' : 'SW1A 1AA'}
                  layoutClassName="flex-1"
                />
              )}
            </form.AppField>
            {region === 'us' ? (
              <form.AppField name="state">
                {(field) => (
                  <field.ComboboxField
                    label="State"
                    placeholder="Select state"
                    layoutClassName="flex-1"
                    options={US_STATES}
                    getKey={(s) => s.code}
                    getLabel={(s) => s.name}
                  />
                )}
              </form.AppField>
            ) : (
              <form.AppField name="country">
                {(field) => (
                  <field.InputField label="Country" placeholder="UK" layoutClassName="flex-1" />
                )}
              </form.AppField>
            )}
          </div>

          <form.AppField name="stripeCustomerId">
            {(field) => <field.InputField label="Stripe customer ID" placeholder="cus_..." />}
          </form.AppField>

          <form.AppField name="internalNotes">
            {(field) => (
              <field.TextareaField label="Internal notes" placeholder="Notes (staff only)" />
            )}
          </form.AppField>
        </div>

        <div className="pt-3 flex items-center gap-x-2 justify-end">
          <Button variant="outline" size="lg" type="button" onClick={onCancel}>
            Cancel
          </Button>
          <form.Subscribe selector={(s) => s.isSubmitting}>
            {(isSubmitting) => (
              <Button type="submit" size="lg" disabled={isSubmitting}>
                {isSubmitting ? 'Adding...' : 'Add Member'}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </form.AppForm>
    </form>
  )
}
